import React, { useState, useEffect } from 'react';
import AbilityPanel from '../components/AbilityPanel';
import GameBoard from '../components/GameBoard';
import { ref, onValue, update, get } from 'firebase/database';
import { database } from '../services/firebaseConfig';
import { IoFlagOutline, IoExitOutline } from 'react-icons/io5';

const GameRoom_Mobile = ({
  gameData,
  playerAbilities,
  activeAbility,
  annihilateVertical,
  setActiveAbility,
  setAnnihilateVertical,
  onNavigateHome,
  // Game state props for progress display
  isMyTurn = false,
  isPaused = false,
  playerName = 'You',
  playerShipsRemaining = 0,
  opponentShipsRemaining = 0,
  onTurnTimeout = () => { },
  onGameTimeout = () => { },
  // Grid props
  playerGrid = [],
  opponentGrid = [],
  gridSize = 8,
  handleAttack = () => { },
  hackerResult = null
}) => {// Game timer state
  const [gameTimeLeft, setGameTimeLeft] = useState(15 * 60); // 15 minutes in seconds
  const [turnTimeLeft, setTurnTimeLeft] = useState(30); // 30 seconds
  const roomId = localStorage.getItem('battleshipRoomId');
  const playerId = localStorage.getItem('battleshipPlayerId');

  // Emoji/Message state
  const [messages, setMessages] = useState([]);
  const [lastMessageTime, setLastMessageTime] = useState(0);
  const [isCooldown, setIsCooldown] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time continuously to auto-expire messages
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 100);
    return () => clearInterval(interval);
  }, []);

  // Send emoji or message
  const sendMessage = async (content, type = 'emoji') => {
    const now = Date.now();

    // Check cooldown (4 seconds)
    if (now - lastMessageTime < 4000) {
      console.log('Cooldown active, please wait');
      return;
    }

    if (!roomId || !playerId) return;

    try {
      const messageData = {
        content,
        type, // 'emoji' or 'message'
        senderId: playerId,
        senderName: playerName,
        timestamp: now
      };

      // Push to Firebase
      await update(ref(database, `rooms/${roomId}/messages/${now}`), messageData);

      setLastMessageTime(now);
      setIsCooldown(true);

      // Remove cooldown after 4 seconds
      setTimeout(() => setIsCooldown(false), 4000);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Listen for messages
  useEffect(() => {
    if (!roomId) return;

    const messagesRef = ref(database, `rooms/${roomId}/messages`);
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const messageList = Object.values(data)
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 10); // Keep last 10 messages
        setMessages(messageList);
      }
    });

    return () => unsubscribe();
  }, [roomId]);

  // Initialize timer in Firebase when game starts
  useEffect(() => {
    if (!roomId) return;

    const gameTimerRef = ref(database, `rooms/${roomId}/gameTimer`);
    onValue(gameTimerRef, (snapshot) => {
      if (!snapshot.exists() && !gameData?.gameOver) {
        // Initialize the timer with 15 minutes
        update(ref(database, `rooms/${roomId}`), {
          gameTimer: {
            timeRemaining: 15 * 60, // 15 minutes in seconds
            isPaused: isPaused || false
          }
        });
      }
    }, { onlyOnce: true });
  }, [roomId, gameData?.gameOver, isPaused]);

  // Load and sync with timer state from Firebase
  useEffect(() => {
    if (!roomId) return;

    const gameTimerRef = ref(database, `rooms/${roomId}/gameTimer`);
    const unsubscribe = onValue(gameTimerRef, (snapshot) => {
      const timerData = snapshot.val();
      if (timerData) {
        // Directly use the timeRemaining value from Firebase
        setGameTimeLeft(timerData.timeRemaining || 15 * 60);

        // If time runs out, trigger game over
        if (timerData.timeRemaining <= 0 && !gameData?.gameOver) {
          onGameTimeout();
        }
      }
    });

    return () => unsubscribe();
  }, [roomId, gameData?.gameOver, onGameTimeout]);

  // Handle the Firebase game timer countdown - only one player should decrement
  useEffect(() => {
    if (gameData?.gameOver || !roomId || isPaused) return;

    // Only let player 1 handle the countdown to avoid conflicts
    const playerId = localStorage.getItem('battleshipPlayerId');
    const players = gameData?.players ? Object.keys(gameData.players) : [];
    const isPlayer1 = players.length > 0 && playerId === players[0];

    if (!isPlayer1) return;

    const timerInterval = setInterval(() => {
      // Get the latest time from Firebase before updating
      get(ref(database, `rooms/${roomId}/gameTimer`)).then((snapshot) => {
        const timerData = snapshot.val();
        if (timerData && timerData.timeRemaining > 0 && !timerData.isPaused) {
          const newTimeRemaining = timerData.timeRemaining - 1;
          update(ref(database, `rooms/${roomId}/gameTimer`), {
            timeRemaining: newTimeRemaining,
            isPaused: isPaused || false
          });
        }
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [gameData?.gameOver, gameData?.players, isPaused, onGameTimeout, roomId]);

  // Update pause state in Firebase
  useEffect(() => {
    if (!roomId || gameData?.gameOver || isPaused === undefined) return;

    update(ref(database, `rooms/${roomId}/gameTimer`), {
      isPaused: isPaused
    });
  }, [isPaused, roomId, gameData?.gameOver]);

  // Turn timer logic - reset when turn changes and sync with Firebase
  useEffect(() => {
    if (!roomId) return;

    const turnTimerRef = ref(database, `rooms/${roomId}/turnTimer`);
    const unsubscribe = onValue(turnTimerRef, (snapshot) => {
      const turnData = snapshot.val();
      if (turnData) {
        setTurnTimeLeft(turnData.timeRemaining || 30);

        // If turn time runs out, trigger turn timeout
        if (turnData.timeRemaining <= 0 && !gameData?.gameOver) {
          // Only trigger timeout if this is actually the current player's turn
          const currentPlayerId = localStorage.getItem('battleshipPlayerId');
          if (gameData?.currentTurn === currentPlayerId) {
            onTurnTimeout();
          }
        }
      }
    });

    return () => unsubscribe();
  }, [roomId, isMyTurn, gameData?.gameOver, gameData?.currentTurn, onTurnTimeout]);

  // Initialize turn timer when turn changes
  useEffect(() => {
    if (!roomId || gameData?.gameOver) return;

    const timeLimit = gameData?.settings?.turnTimeLimit || 30;
    const playerId = localStorage.getItem('battleshipPlayerId');

    // Initialize turn timer in Firebase
    update(ref(database, `rooms/${roomId}/turnTimer`), {
      timeRemaining: timeLimit,
      isPaused: isPaused || false,
      currentPlayer: isMyTurn ? playerId : null
    });

    setTurnTimeLeft(timeLimit);
  }, [isMyTurn, gameData?.settings?.turnTimeLimit, roomId, gameData?.gameOver, isPaused]);

  // Handle turn timer countdown - only the current player decrements
  useEffect(() => {
    if (!isMyTurn || isPaused || gameData?.gameOver || !roomId) return;

    const turnInterval = setInterval(() => {
      get(ref(database, `rooms/${roomId}/turnTimer`)).then((snapshot) => {
        const turnData = snapshot.val();
        if (turnData && turnData.timeRemaining > 0 && !turnData.isPaused) {
          const newTimeRemaining = turnData.timeRemaining - 1;
          update(ref(database, `rooms/${roomId}/turnTimer`), {
            timeRemaining: newTimeRemaining,
            isPaused: isPaused || false
          });

          if (newTimeRemaining <= 0) {
            // Only trigger timeout if this is actually the current player's turn
            const currentPlayerId = localStorage.getItem('battleshipPlayerId');
            if (gameData?.currentTurn === currentPlayerId) {
              onTurnTimeout();
            }
          }
        }
      });
    }, 1000);

    return () => clearInterval(turnInterval);
  }, [isMyTurn, isPaused, gameData?.gameOver, gameData?.currentTurn, onTurnTimeout, roomId]);
  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle surrender with Firebase notification
  const handleSurrender = async () => {
    if (window.confirm('Are you sure you want to surrender?')) {
      try {
        if (roomId && gameData) {
          const playerId = localStorage.getItem('battleshipPlayerId');
          const playerName = localStorage.getItem('battleshipPlayerName') || 'Player';

          // Update game state to show surrender
          await update(ref(database, `rooms/${roomId}`), {
            gameOver: true,
            winner: Object.keys(gameData.players).find(id => id !== playerId),
            endReason: 'surrender',
            surrenderedBy: playerId,
            endTime: Date.now(),
            notifications: {
              [`surrender_${Date.now()}`]: {
                type: 'surrender',
                playerName: playerName,
                message: `${playerName} has surrendered the game`,
                timestamp: Date.now()
              }
            }
          });
        }
        onNavigateHome?.();
      } catch (error) {
        console.error('Error surrendering:', error);
        onNavigateHome?.();
      }
    }
  };

  // Handle leave with Firebase notification
  const handleLeave = async () => {
    if (window.confirm('Are you sure you want to leave?')) {
      try {
        if (roomId && gameData) {
          const playerId = localStorage.getItem('battleshipPlayerId');
          const playerName = localStorage.getItem('battleshipPlayerName') || 'Player';

          // Update game state to show player left
          await update(ref(database, `rooms/${roomId}`), {
            gameOver: true,
            winner: Object.keys(gameData.players).find(id => id !== playerId),
            endReason: 'leave',
            leftBy: playerId,
            endTime: Date.now(),
            notifications: {
              [`leave_${Date.now()}`]: {
                type: 'leave',
                playerName: playerName,
                message: `${playerName} has left the game`,
                timestamp: Date.now()
              }
            }
          });
        }
        onNavigateHome?.();
      } catch (error) {
        console.error('Error leaving:', error);
        onNavigateHome?.();
      }
    }
  };

  return (
    <div className="h-screen bg-gradient-to-br from-gray-950 via-black to-gray-900 text-white flex flex-col overflow-hidden">
      {/* 1. TOP HEADER */}
      <div className="h-14 bg-gray-900/30 border-2 border-gray-700/50 rounded-lg flex items-center justify-center shrink-0 shadow-2xl backdrop-blur-sm mt-3 mx-3">
        {/* Square 1 - Surrender */}
        <div className="h-full aspect-square bg-gray-900/30 border-2 border-gray-700/50 shadow-2xl backdrop-blur-sm flex items-center justify-center">
          <button
            onClick={handleSurrender}
            className="w-full h-full flex items-center justify-center hover:bg-red-900/30 transition-colors"
            title="Surrender"
          >
            <IoFlagOutline className="text-red-400" size={20} />
          </button>
        </div>

        {/* Rectangle 1 - Game Timer */}
        <div className="h-full flex-1 bg-gray-900/30 border-2 border-gray-700/50 shadow-2xl backdrop-blur-sm flex items-center justify-center">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Game</span>
            <span className={`font-mono font-bold text-sm ${gameTimeLeft < 60 ? 'text-red-400 animate-pulse' : 'text-gray-200'}`}>
              {formatTime(gameTimeLeft)}
            </span>
          </div>
        </div>

        {/* Rectangle 2 - Turn Timer */}
        <div className="h-full flex-1 bg-gray-900/30 border-2 border-gray-700/50 shadow-2xl backdrop-blur-sm flex items-center justify-center">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold ${isMyTurn ? 'text-green-400' : 'text-yellow-400'}`}>
              {isMyTurn ? 'YOUR TURN' : 'OPP TURN'}
            </span>
            {isMyTurn && (
              <span className={`font-mono font-bold text-sm ${turnTimeLeft <= 10 ? 'text-red-400 animate-pulse' : 'text-green-400'}`}>
                {turnTimeLeft}s
              </span>
            )}
          </div>
        </div>

        {/* Square 2 - Leave */}
        <div className="h-full aspect-square bg-gray-900/30 border-2 border-gray-700/50 shadow-2xl backdrop-blur-sm flex items-center justify-center">
          <button
            onClick={handleLeave}
            className="w-full h-full flex items-center justify-center hover:bg-gray-800/50 transition-colors"
            title="Leave Game"
          >
            <IoExitOutline className="text-gray-400" size={20} />
          </button>
        </div>
      </div>

      {/* 2. ENEMY GRID */}
      <div className="flex-1 min-h-0 flex items-center justify-center mt-3 mx-3">
        <div className="aspect-square max-h-full max-w-full w-full bg-gray-900/30 border-2 border-gray-700/50 rounded-lg p-4 flex items-center justify-center shadow-2xl backdrop-blur-sm relative">
          <div className="w-full h-full">
            <GameBoard
              grid={opponentGrid}
              gridSize={gridSize}
              isPlayerGrid={false}
              activeAbility={activeAbility}
              annihilateVertical={annihilateVertical}
              onCellClick={handleAttack}
              hackerResult={hackerResult}
              showLabels={true}
            />
          </div>

          {/* Message Overlay - Shows messages < 2 seconds old */}
          <div className="absolute bottom-10 left-0 right-0 flex flex-col-reverse items-center gap-4 pointer-events-none z-30">
            {messages
              .filter(msg => currentTime - msg.timestamp < 2000)
              .slice(0, 5) // Show up to 5 overlapping messages
              .map((msg, i) => (
                <div
                  key={`${msg.timestamp}-${i}`}
                  className="animate-pop-in text-white flex items-center justify-center transform transition-all"
                  style={{
                    textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 0 10px rgba(0,0,0,0.5)'
                  }}
                >
                  <span className="text-4xl font-bold animate-bounce-subtle">{msg.content}</span>
                </div>
              ))}
          </div>

          {/* Animation Styles */}
          <style>{`
            @keyframes popIn {
              0% { opacity: 0; transform: scale(0.5) translateY(20px); }
              50% { transform: scale(1.2) translateY(-10px); }
              100% { opacity: 1; transform: scale(1) translateY(0); }
            }
            @keyframes bounceSubtle {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-5px); }
            }
            .animate-pop-in {
              animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
            }
            .animate-bounce-subtle {
              animation: bounceSubtle 2s infinite ease-in-out;
            }
          `}</style>
        </div>
      </div>

      {/* 3. INFO ROW */}
      <div className="h-32 flex gap-3 shrink-0 mt-3 mx-3">
        {/* MyGrid - 40% -> Fixed to aspect-square */}
        <div className="h-full aspect-square bg-gray-900/30 backdrop-blur-sm border-2 border-gray-700/50 rounded-lg shadow-xl p-2">
          <div className="w-full h-full">
            <GameBoard
              grid={playerGrid}
              gridSize={gridSize}
              isPlayerGrid={true}
              activeAbility={activeAbility}
              onCellClick={null}
              playerData={{}}
            />
          </div>
        </div>

        {/* Ships Stats - 10% */}
        <div className="h-full w-[10%] bg-gray-900/30 backdrop-blur-sm border-2 border-gray-700/50 rounded-lg shadow-xl flex items-center justify-center gap-2 p-2">
          {/* Player Ships Bar */}
          <div className="flex-1 h-full flex flex-col-reverse gap-1">
            {[1, 2, 3, 4, 5].map((segment) => (
              <div
                key={`player-ship-${segment}`}
                className={`flex-1 rounded-sm ${segment <= playerShipsRemaining
                  ? 'bg-blue-500/80 border border-blue-400/50'
                  : 'bg-gray-700/30 border border-gray-600/30'
                  }`}
              />
            ))}
          </div>

          {/* Enemy Ships Bar */}
          <div className="flex-1 h-full flex flex-col-reverse gap-1">
            {[1, 2, 3, 4, 5].map((segment) => (
              <div
                key={`enemy-ship-${segment}`}
                className={`flex-1 rounded-sm ${segment <= opponentShipsRemaining
                  ? 'bg-red-500/80 border border-red-400/50'
                  : 'bg-gray-700/30 border border-gray-600/30'
                  }`}
              />
            ))}
          </div>
        </div>

        {/* Emoji/Messages - Flex-1 to take remaining space */}
        <div className="h-full flex-1 bg-gray-900/30 backdrop-blur-sm border-2 border-gray-700/50 rounded-lg shadow-xl overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent p-2">
          <div className="flex flex-col gap-2 h-full min-w-max">
            {/* Layer 1 - Emojis */}
            <div className="flex-1 flex gap-2 items-center">
              {['😀', '😎', '😱', '💥', '🎯', '⚡', '🔥', '💪', '👍', '🚀', '😂', '🤔', '😊', '🎉'].map((emoji, i) => (
                <button
                  key={`emoji-${i}`}
                  onClick={() => sendMessage(emoji, 'emoji')}
                  disabled={isCooldown}
                  className={`shrink-0 w-10 h-10 bg-gray-800/50 hover:bg-gray-700/70 rounded border border-gray-600/30 flex items-center justify-center transition-all hover:scale-110 text-lg ${isCooldown ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {emoji}
                </button>
              ))}
            </div>

            {/* Layer 2 - Quick Messages */}
            <div className="flex-1 flex gap-2 items-center">
              {['GG', 'Nice!', 'Ouch', 'Lucky', 'GLHF', 'Rematch?', 'Close!', 'Well played', 'Thanks!', 'No way!'].map((msg, i) => (
                <button
                  key={`msg-${i}`}
                  onClick={() => sendMessage(msg, 'message')}
                  disabled={isCooldown}
                  className={`shrink-0 px-4 h-10 bg-gray-800/50 hover:bg-gray-700/70 rounded border border-gray-600/30 flex items-center justify-center transition-all hover:scale-105 text-sm text-gray-300 whitespace-nowrap font-medium ${isCooldown ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {msg}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 4. ABILITIES FOOTER */}
      {playerAbilities && Object.keys(playerAbilities).length > 0 && (
        <div className="h-[16%] shrink-0 mt-3">
          <AbilityPanel
            abilities={playerAbilities}
            onUseAbility={setActiveAbility}
            activeAbility={activeAbility}
            annihilateVertical={annihilateVertical}
            onToggleAnnihilateOrientation={() => setAnnihilateVertical(!annihilateVertical)}
            isMyTurn={isMyTurn}
          />
        </div>
      )}
    </div>
  );
};

export default GameRoom_Mobile;
